/* eslint-disable react/jsx-max-props-per-line */
import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Card,
  CardActionArea,
  LinearProgress,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import EditNoteIcon from '@mui/icons-material/EditNote';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  COMPARACAO_META_COPY,
  getVolumeLabel,
  KPI_KEYS,
  UNIDADE_LABELS,
} from './constants';
import { computeMetaPeriodoPessoal } from './metaPessoalSession';

const VALUE_SIZE = '1.2rem';

const metaColor = (comparacao) => {
  if (comparacao === 'acima') return 'success';
  if (comparacao === 'abaixo') return 'warning';
  return 'info';
};

const iconWrapSx = (color) => ({
  width: 28,
  height: 28,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  bgcolor: (theme) => {
    if (color === 'info') {
      return theme.palette.mode === 'dark' ? 'rgba(33, 150, 243, 0.16)' : 'rgba(33, 150, 243, 0.12)';
    }
    if (color === 'primary') {
      return theme.palette.mode === 'dark' ? 'rgba(63, 81, 181, 0.2)' : 'rgba(63, 81, 181, 0.1)';
    }
    return theme.palette.mode === 'dark' ? 'action.hover' : 'grey.100';
  },
});

const MetricTile = ({
  label,
  value,
  icon,
  iconColor,
  loading,
  footer,
  onClick,
  active,
  children,
}) => {
  const body = (
    <Box sx={{ p: 1.5, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={0.75}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              fontWeight: 600,
              fontSize: '0.625rem',
              lineHeight: 1.2,
              display: 'block',
            }}
          >
            {label}
          </Typography>
          <Box sx={{ mt: 0.75 }}>
            {loading ? (
              <Skeleton width={64} height={26} sx={{ borderRadius: 1 }} />
            ) : (
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  fontSize: VALUE_SIZE,
                  lineHeight: 1,
                  color: active ? 'info.main' : 'text.primary',
                }}
              >
                {value ?? '—'}
              </Typography>
            )}
          </Box>
        </Box>
        <Box sx={iconWrapSx(iconColor)}>
          {React.cloneElement(icon, {
            sx: {
              fontSize: 16,
              color: iconColor === 'info' ? 'info.main'
                : iconColor === 'primary' ? 'primary.main'
                  : 'text.secondary',
            },
          })}
        </Box>
      </Stack>

      {children}

      {!loading && footer && (
        <Box sx={{ mt: 'auto', pt: 0.75 }}>
          {footer}
        </Box>
      )}
    </Box>
  );

  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        borderRadius: 2,
        borderWidth: active ? 2 : 1,
        borderColor: active ? 'info.main' : 'divider',
        boxShadow: (theme) => (theme.palette.mode === 'dark' ? 2 : 1),
        ...(onClick && {
          transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
          '&:hover': {
            borderColor: 'info.main',
            boxShadow: (theme) => `0 0 0 1px ${theme.palette.info.main}40`,
          },
        }),
      }}
    >
      {onClick ? (
        <CardActionArea
          onClick={onClick}
          sx={{ height: '100%', alignItems: 'stretch', borderRadius: 2 }}
        >
          {body}
        </CardActionArea>
      ) : (
        body
      )}
    </Card>
  );
};

MetricTile.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  icon: PropTypes.element.isRequired,
  iconColor: PropTypes.oneOf(['primary', 'info', 'default']),
  loading: PropTypes.bool,
  footer: PropTypes.node,
  onClick: PropTypes.func,
  active: PropTypes.bool,
  children: PropTypes.node,
};

MetricTile.defaultProps = {
  value: null,
  iconColor: 'default',
  loading: false,
  footer: null,
  onClick: null,
  active: false,
  children: null,
};

const PeriodoMetricStrip = ({
  resumo,
  lente,
  loading,
  onKpiClick,
  alvoDiario,
}) => {
  const volumeLabel = lente ? getVolumeLabel(lente) : getVolumeLabel(resumo);
  const unidade = lente?.unidade ?? resumo.unidade ?? 'servicos';
  const unitLabel = UNIDADE_LABELS[unidade] ?? 'serviços';
  const metaDiaria = (
    resumo.lenteId === lente?.id ? resumo.metaDiaria : null
  ) ?? lente?.metaDiaria;
  const alvo = alvoDiario ?? metaDiaria;
  const totalDigitado = resumo.totalDigitado ?? resumo.servicosDigitados;
  const metaPeriodoOficial = resumo.metaPeriodo ?? resumo.meuPeriodo?.metaPeriodo;
  const metaPeriodoBar = computeMetaPeriodoPessoal(alvo, resumo.diasUteis)
    ?? metaPeriodoOficial;
  const percentual = metaPeriodoBar && metaPeriodoBar > 0
    ? Math.round((totalDigitado / metaPeriodoBar) * 100)
    : resumo.percentualMeta ?? null;
  const comparacao = percentual == null
    ? (resumo.comparacaoMeta ?? resumo.meuPeriodo?.comparacaoMedia)
    : percentual > 100 ? 'acima'
      : percentual < 100 ? 'abaixo'
        : 'na_media';
  const showPiso = alvo != null && metaDiaria != null && alvo !== metaDiaria;

  return (
    <Box
      sx={{
        display: 'grid',
        gap: 1.25,
        gridTemplateColumns: {
          xs: 'repeat(2, 1fr)',
          md: 'repeat(4, 1fr)',
        },
        alignItems: 'stretch',
      }}
    >
      <MetricTile
        label={volumeLabel}
        value={totalDigitado}
        icon={<EditNoteIcon />}
        iconColor="primary"
        loading={loading}
        footer={(
          <Typography variant="caption" color="text.secondary">
            {metaPeriodoBar != null
              ? `Alvo do ciclo: ${metaPeriodoBar} ${unitLabel}`
              : `No ciclo atual`}
          </Typography>
        )}
      />

      <MetricTile
        label="Média / dia útil"
        value={resumo.mediaDiaUtil}
        icon={<TrendingUpIcon />}
        iconColor="info"
        loading={loading}
        onClick={() => onKpiClick(KPI_KEYS.MEDIA)}
        footer={(
          <Stack spacing={0.25}>
            {alvo != null && (
              <Typography variant="caption" color="text.secondary">
                Alvo de {alvo} {unitLabel}
              </Typography>
            )}
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <OpenInNewIcon sx={{ fontSize: 12, color: 'info.main' }} />
              <Typography variant="caption" color="info.main" fontWeight={600}>
                Ver por dia
              </Typography>
            </Stack>
          </Stack>
        )}
      />

      <MetricTile
        label="% da meta"
        value={percentual != null ? `${percentual}%` : null}
        icon={<TrackChangesIcon />}
        iconColor="default"
        loading={loading}
        footer={(
          <Stack spacing={0.75}>
            {!loading && percentual != null && (
              <LinearProgress
                variant="determinate"
                value={Math.min(100, Math.max(0, percentual))}
                color={metaColor(comparacao)}
                sx={{ height: 5, borderRadius: 1 }}
              />
            )}
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={0.5}>
              <Typography variant="caption" color="text.secondary" noWrap>
                {metaPeriodoBar != null
                  ? `${totalDigitado ?? 0}/${metaPeriodoBar}`
                  : ''}
              </Typography>
              {comparacao && COMPARACAO_META_COPY[comparacao] && (
                <Typography
                  variant="caption"
                  color={`${metaColor(comparacao)}.main`}
                  fontWeight={600}
                  noWrap
                >
                  {COMPARACAO_META_COPY[comparacao]}
                </Typography>
              )}
            </Stack>
            {showPiso && (
              <Typography variant="caption" color="text.disabled">
                piso empresa {metaDiaria}
              </Typography>
            )}
          </Stack>
        )}
      />

      <MetricTile
        label="Dias úteis restantes"
        value={resumo.diasUteisRestantes}
        icon={<CalendarTodayIcon />}
        iconColor="default"
        loading={loading}
        onClick={() => onKpiClick(KPI_KEYS.DIAS_UTEIS)}
        footer={(
          <Stack spacing={0.25}>
            <Typography variant="caption" color="text.secondary">
              {resumo.diasUteis != null && resumo.diasUteisGlobais != null
                ? `${resumo.diasUteis}/${resumo.diasUteisGlobais} no ciclo`
                : resumo.diasUteis != null
                  ? `${resumo.diasUteis} dias no ciclo`
                  : 'Até o fim da medição'}
            </Typography>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <OpenInNewIcon sx={{ fontSize: 12, color: 'info.main' }} />
              <Typography variant="caption" color="info.main" fontWeight={600}>
                Ver dias do ciclo
              </Typography>
            </Stack>
          </Stack>
        )}
      />
    </Box>
  );
};

PeriodoMetricStrip.propTypes = {
  resumo: PropTypes.object.isRequired,
  lente: PropTypes.shape({
    id: PropTypes.string,
    label: PropTypes.string,
    unidade: PropTypes.string,
    metaDiaria: PropTypes.number,
  }),
  loading: PropTypes.bool,
  onKpiClick: PropTypes.func.isRequired,
  alvoDiario: PropTypes.number,
};

PeriodoMetricStrip.defaultProps = {
  lente: null,
  loading: false,
  alvoDiario: null,
};

export default PeriodoMetricStrip;
